import { z } from "zod";

export const insuranceTypes = [
  "PERSONAL_LIABILITY",
  "CONTENTS",
  "BUILDINGS",
  "VALUABLES",
  "GLASS",
  "HOME_CONSTRUCTION",
  "RECREATIONAL_HOME",
  "HOUSEBOAT",
  "CAR",
  "MOTORCYCLE",
  "MOPED",
  "BICYCLE",
  "MOBILITY_SCOOTER",
  "MICRO_MOBILITY",
  "CLASSIC_VEHICLE",
  "CARAVAN",
  "CAMPER",
  "TRAILER",
  "BOAT",
  "BREAKDOWN_ASSISTANCE",
  "VEHICLE_OCCUPANTS",
  "RENTAL_VEHICLE_EXCESS",
  "TRAVEL",
  "TRAVEL_CANCELLATION",
  "HEALTH_BASIC",
  "HEALTH_SUPPLEMENTARY",
  "DENTAL",
  "INTERNATIONAL_HEALTH",
  "ACCIDENT",
  "LEGAL_EXPENSES",
  "PET",
  "HORSE",
  "TERM_LIFE",
  "LIFE",
  "FUNERAL",
  "DISABILITY",
  "PAYMENT_PROTECTION",
  "CYBER",
  "ELECTRONICS",
  "WEDDING_EVENT",
  "PACKAGE",
  "OTHER_PERSONAL",
] as const;

export const insuranceProductKinds = [
  "STANDALONE",
  "ADD_ON",
  "BUNDLE",
] as const;

export const insuranceProductStatuses = [
  "DRAFT",
  "PUBLISHED",
  "ARCHIVED",
] as const;

export const insuranceAvailabilityOptions = [
  "AVAILABLE",
  "NOT_ACCEPTING_NEW_CUSTOMERS",
  "DISCONTINUED",
  "UNKNOWN",
] as const;

export const insuranceAffiliateStatuses = ["NONE", "ACTIVE", "PAUSED"] as const;

export const insuranceChannels = [
  "WEBSITE",
  "APP",
  "PHONE",
  "ADVISER",
] as const;

export const insuranceCoverageStatuses = [
  "INCLUDED",
  "OPTIONAL",
  "EXCLUDED",
  "CONDITIONAL",
  "UNKNOWN",
] as const;

export const insuranceEligibilityStatuses = [
  "ELIGIBLE",
  "INELIGIBLE",
  "CONDITIONAL",
  "UNKNOWN",
] as const;

export type InsuranceType = (typeof insuranceTypes)[number];

export type InsuranceProductKind = (typeof insuranceProductKinds)[number];

export type InsuranceAvailability =
  (typeof insuranceAvailabilityOptions)[number];

export type InsuranceChannel = (typeof insuranceChannels)[number];

export const keySchema = z.string().regex(/^[a-z][a-z0-9_]*$/);

export const sourceIdsSchema = z.array(keySchema).min(1);

export const currencySchema = z.string().regex(/^[A-Z]{3}$/);

export const durationSchema = z.object({
  value: z.number().int().nonnegative(),
  unit: z.enum(["HOURS", "DAYS", "WEEKS", "MONTHS", "YEARS"]),
});

export const insuranceRelatedProductSchema = z.object({
  productId: z.string().min(1),
  relationship: z.enum(["BUNDLE_COMPONENT", "AVAILABLE_WITH"]),
});

export const insuranceFactSchema = z.object({
  key: keySchema,
  value: z.union([
    z.number().finite(),
    z.boolean(),
    keySchema,
    z.array(keySchema),
  ]),
  unit: keySchema.nullish(),
  sourceIds: sourceIdsSchema,
});

export const limitScopeSchema = z.enum([
  "PER_CLAIM",
  "PER_YEAR",
  "PER_ITEM",
  "PER_PERSON",
  "PER_TRIP",
  "PER_DAY",
  "PER_POLICY",
  "LIFETIME",
  "OTHER",
]);

export const insuranceSourceSchema = z.object({
  id: keySchema,
  kind: z.enum([
    "PRODUCT_PAGE",
    "POLICY_WORDING",
    "IPID",
    "PRICING",
    "FAQ",
    "OTHER_OFFICIAL",
  ]),
  url: z.string().url(),
  title: z.string().min(1),
  language: z.string().min(2),
  version: z.string().min(1).nullish(),
  effectiveFrom: z.string().date().nullish(),
  checkedAt: z.string().datetime({ offset: true }),
});

export const insurancePricingSchema = z
  .object({
    currency: currencySchema,
    basis: z.enum(["PERSONAL_QUOTE", "ADVERTISED_FROM", "FIXED"]),
    amount: z.number().nonnegative().nullish(),
    billingPeriod: z
      .enum(["MONTHLY", "QUARTERLY", "YEARLY", "ONE_TIME"])
      .nullish(),
    // Stable keys; explain each price assumption in localized factNotes.
    assumptionKeys: z.array(keySchema),
    sourceIds: sourceIdsSchema,
    checkedAt: z.string().datetime({ offset: true }),
  })
  .superRefine((pricing, ctx) => {
    if (
      pricing.basis !== "PERSONAL_QUOTE" &&
      (pricing.amount == null ||
        pricing.billingPeriod == null ||
        pricing.assumptionKeys.length === 0)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Published prices require an amount, billing period, and assumption keys.",
      });
    }
    if (pricing.basis === "PERSONAL_QUOTE" && pricing.amount != null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["amount"],
        message: "A personal quote is not a published product price.",
      });
    }
  });

export const insuranceEligibilitySchema = z
  .object({
    minimumAge: z.number().int().nonnegative().nullish(),
    maximumAge: z.number().int().nonnegative().nullish(),
    residencyRequired: z.boolean().nullish(),
    householdTypes: z
      .array(
        z.enum([
          "SINGLE",
          "COUPLE",
          "FAMILY",
          "SINGLE_PARENT",
          "SHARED_HOUSEHOLD",
        ]),
      )
      .nullish(),
    sharedAccommodation: z.enum(insuranceEligibilityStatuses).nullish(),
    students: z.enum(insuranceEligibilityStatuses).nullish(),
    // Labels and qualifications for document/condition keys belong in factNotes.
    requiredDocumentKeys: z.array(keySchema).nullish(),
    conditionKeys: z.array(keySchema),
    sourceIds: sourceIdsSchema,
  })
  .refine(
    (value) =>
      value.minimumAge == null ||
      value.maximumAge == null ||
      value.minimumAge <= value.maximumAge,
    {
      message: "Maximum age must not be below minimum age.",
      path: ["maximumAge"],
    },
  );

export const insuranceLimitSchema = z.object({
  key: keySchema,
  amount: z.number().nonnegative(),
  currency: currencySchema,
  scope: limitScopeSchema,
});

export const insuranceQuantityLimitSchema = z.object({
  key: keySchema,
  value: z.number().nonnegative(),
  unit: z.enum([
    "HOURS",
    "DAYS",
    "NIGHTS",
    "WEEKS",
    "MONTHS",
    "YEARS",
    "SESSIONS",
    "ITEMS",
    "KILOMETERS",
  ]),
  scope: limitScopeSchema,
});

export const insuranceDeductibleSchema = z
  .object({
    key: keySchema,
    basis: z.enum(["FIXED_AMOUNT", "PERCENTAGE", "COMBINED"]),
    amount: z.number().nonnegative().nullish(),
    currency: currencySchema.nullish(),
    percentage: z.number().min(0).max(100).nullish(),
    minimumAmount: z.number().nonnegative().nullish(),
    maximumAmount: z.number().nonnegative().nullish(),
    scope: limitScopeSchema.nullish(),
    // Explain applicability, percentage basis, and combined formulas in factNotes.
  })
  .superRefine((value, ctx) => {
    const moneyRequired = value.basis !== "PERCENTAGE";
    const percentageRequired = value.basis !== "FIXED_AMOUNT";
    if (
      (moneyRequired && value.amount == null) ||
      (percentageRequired && value.percentage == null) ||
      (value.basis === "FIXED_AMOUNT" && value.percentage != null) ||
      (value.basis === "PERCENTAGE" && value.amount != null)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Deductible values must match their basis.",
      });
    }
    if (
      (value.amount != null ||
        value.minimumAmount != null ||
        value.maximumAmount != null) &&
      value.currency == null
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["currency"],
        message: "Monetary deductibles require a currency.",
      });
    }
    if (
      value.minimumAmount != null &&
      value.maximumAmount != null &&
      value.minimumAmount > value.maximumAmount
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["maximumAmount"],
        message: "Maximum must not be below minimum.",
      });
    }
  });

export const insuranceCoverageItemSchema = z
  .object({
    key: keySchema,
    status: z.enum(insuranceCoverageStatuses),
    // Omitted/null means unknown; an empty array is not an unlimited-cover claim.
    limits: z.array(insuranceLimitSchema).nullish(),
    quantityLimits: z.array(insuranceQuantityLimitSchema).nullish(),
    reimbursementPercentage: z.number().min(0).max(100).nullish(),
    waitingPeriod: durationSchema.nullish(),
    deductibles: z.array(insuranceDeductibleSchema).nullish(),
    sourceIds: z.array(keySchema),
  })
  .refine((value) => value.status === "UNKNOWN" || value.sourceIds.length > 0, {
    message: "Known coverage statuses require official sources.",
    path: ["sourceIds"],
  });

export const insuranceContractSchema = z.object({
  minimumTerm: durationSchema.nullish(),
  waitingPeriod: durationSchema.nullish(),
  renewalModel: z
    .enum(["AUTOMATIC", "MANUAL", "NO_RENEWAL", "UNKNOWN"])
    .nullish(),
  cancellationNotice: durationSchema.nullish(),
  coolingOffPeriod: durationSchema.nullish(),
  startDateRuleKeys: z.array(keySchema),
  conditionKeys: z.array(keySchema),
  sourceIds: sourceIdsSchema,
});

export const insuranceCoverageTextSchema = z.object({
  key: keySchema,
  label: z.string().min(1),
  explanation: z.string().min(1),
  conditions: z.string().min(1).nullish(),
});

export const insuranceFactNotesSchema = z.record(keySchema, z.string().min(1));

export type InsuranceRelatedProduct = z.infer<
  typeof insuranceRelatedProductSchema
>;

export type InsuranceFact = z.infer<typeof insuranceFactSchema>;

export type InsuranceSource = z.infer<typeof insuranceSourceSchema>;

export type InsurancePricing = z.infer<typeof insurancePricingSchema>;

export type InsuranceEligibility = z.infer<typeof insuranceEligibilitySchema>;

export type InsuranceCoverageItem = z.infer<typeof insuranceCoverageItemSchema>;

export type InsuranceContract = z.infer<typeof insuranceContractSchema>;

export type InsuranceCoverageText = z.infer<typeof insuranceCoverageTextSchema>;

export type InsuranceFactNotes = z.infer<typeof insuranceFactNotesSchema>;
